import React from 'react';
import { useToast } from '../../../../context/ToastContext';
import { useConfirm } from '../../../../context/ConfirmContext';
import { testConnection } from '../api';

export default function LinkedInSettings({ connection, onUpdate }) {
  const { addToast } = useToast();
  const { confirm } = useConfirm();
  const [loading, setLoading] = React.useState(false);
  const [testLoading, setTestLoading] = React.useState(false);
  const [settings, setSettings] = React.useState({
    name: connection.name
  });

  // Update settings when connection changes
  React.useEffect(() => {
    setSettings({
      name: connection.name
    });
  }, [connection]);

  const handleResetConnection = async () => {
    const confirmed = await confirm({
      title: 'Reset Connection',
      message: 'Are you sure you want to reset this connection? You will need to reconnect with LinkedIn.',
      confirmText: 'Reset Connection',
      confirmVariant: 'danger'
    });

    if (confirmed) {
      try {
        await onUpdate({
          ...connection,
          credentials: null,
          settings: {
            name: connection.name
          },
          is_configured: false
        });
        addToast('Connection reset successfully', 'success');
      } catch (error) {
        addToast('Failed to reset connection', 'error');
      }
    }
  };

  const handleTestConnection = async () => {
    setTestLoading(true);
    try {
      const result = await testConnection(connection.credentials);
      if (result.success) {
        addToast('Connection test successful', 'success');
      } else {
        throw new Error(result.error || 'Connection test failed');
      }
    } catch (error) {
      console.error('Test connection error:', error);
      addToast('Connection test failed: ' + error.message, 'error');
    } finally {
      setTestLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onUpdate({
        ...connection,
        name: settings.name.trim(),
        settings: {
          ...connection.settings,  // Preserve existing settings
          name: settings.name.trim()
        },
        updated_at: new Date().toISOString()
      });
      addToast('Settings updated successfully', 'success');
    } catch (error) {
      addToast('Failed to update settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">LinkedIn Profile Connection</h3>
        <button
          onClick={handleResetConnection}
          className="px-3 py-1 text-sm text-red-500 hover:text-red-400 transition-colors"
        >
          Reset Connection
        </button>
      </div>

      {/* Profile Information */}
      <div className="bg-dark-300 p-4 rounded-lg space-y-4">
        {/* Profile Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {connection.settings?.picture && (
              <img 
                src={connection.settings.picture}
                alt={connection.settings.name}
                className="w-12 h-12 rounded-full object-cover border border-divider"
              />
            )}
            <div>
              <div className="font-medium">
                {connection.settings?.given_name} {connection.settings?.family_name}
              </div>
              <div className="text-sm text-gray-400">
                {connection.settings?.email}
              </div>
            </div>
          </div>
          <div className="text-sm text-green-500">Connected</div>
        </div>

        {/* Profile ID */}
        <div className="text-sm text-gray-400">
          Profile ID: {connection.settings?.profileId}
        </div>

        {/* Test Connection Button */}
        <div className="pt-4 border-t border-divider">
          <button
            onClick={handleTestConnection}
            disabled={testLoading}
            className="w-full px-4 py-2 bg-accent-secondary text-white rounded hover:bg-accent-secondary/90 disabled:opacity-50 transition-colors"
          >
            {testLoading ? 'Testing Connection...' : 'Test Connection'}
          </button>
        </div>
      </div>

      {/* Connection Settings */}
      <div>
        <label className="block text-sm font-medium mb-2">
          Connection Name
        </label>
        <input
          type="text"
          value={settings.name}
          onChange={(e) => setSettings({ ...settings, name: e.target.value })}
          className="w-full px-3 py-2 bg-dark-300 border border-divider rounded focus:outline-none focus:border-accent-primary"
          required
        />
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          className="px-4 py-2 bg-accent-primary text-white rounded hover:bg-accent-primary/90 disabled:opacity-50"
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
} 